/**
 * Health Integrations Page - OLED Dark Neon Redesign
 *
 * Modern dark theme with glassmorphism, player headshots, and resizable panels.
 * Fixed viewport - no infinite scroll.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Panel, Group as PanelGroup, Separator as PanelResizeHandle } from 'react-resizable-panels';
import {
  Heart,
  Activity,
  Moon,
  Battery,
  Zap,
  Watch,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronRight,
  Users,
  TrendingUp,
  Link2,
  Wifi,
  WifiOff,
  Sparkles,
  GripVertical,
  Brain,
} from 'lucide-react';

// Player headshot mapping - jersey number to filename (sport-aware)
const getPlayerHeadshot = (number, name, sport = 'basketball') => {
  const lastName = name.split(' ').pop();
  const paddedNumber = String(number).padStart(2, '0');
  if (sport === 'baseball') {
    return `/images/baseball/orioles/players/${paddedNumber}_${lastName}.jpg`;
  }
  return `/images/basketball/realmadrid/players/${paddedNumber}_${lastName}.jpg`;
};

// Real Madrid EuroLeague 2024-25 roster with enriched synthetic data
const REAL_MADRID_PLAYERS = [
  { id: '005928', name: 'Facundo Campazzo', position: 'PG', number: 7, whoopConnected: true, ouraConnected: true, neurophoriaConnected: true, readiness: 92, strain: 11.8, hrv: 78, sleep: 8.2, ppg: 12.2, pir: 18.0,
    biometrics: { stressLoad: 32, cortisol: 'optimal', bloodGlucose: 92, ketones: 0.8, vo2Max: 52, lactateThreshold: 168, muscleOxygen: 74, neuralFatigue: 'low', bodyTemp: '+0.2', respiratoryRate: 14, mentalState: 92, focusScore: 88, flowStateReadiness: 85, umpiScore: 91 }},
  { id: '005791', name: 'Walter Tavares', position: 'C', number: 22, whoopConnected: true, ouraConnected: true, neurophoriaConnected: false, readiness: 83, strain: 11.5, hrv: 62, sleep: 7.2, ppg: 10.6, pir: 16.4,
    biometrics: { stressLoad: 48, cortisol: 'elevated', bloodGlucose: 98, ketones: 0.4, vo2Max: 44, lactateThreshold: 155, muscleOxygen: 68, neuralFatigue: 'moderate', bodyTemp: '+0.4', respiratoryRate: 16 }},
  { id: '003108', name: 'Mario Hezonja', position: 'SF', number: 11, whoopConnected: true, ouraConnected: true, neurophoriaConnected: true, readiness: 91, strain: 15.1, hrv: 70, sleep: 7.6, ppg: 14.6, pir: 14.2,
    biometrics: { stressLoad: 38, cortisol: 'optimal', bloodGlucose: 88, ketones: 1.2, vo2Max: 50, lactateThreshold: 172, muscleOxygen: 76, neuralFatigue: 'low', bodyTemp: '+0.1', respiratoryRate: 13, mentalState: 89, focusScore: 91, flowStateReadiness: 78, umpiScore: 87 }},
  { id: '007920', name: 'Dzanan Musa', position: 'SF', number: 1, whoopConnected: true, ouraConnected: false, neurophoriaConnected: false, readiness: 85, strain: 14.2, hrv: 65, sleep: 7.1, ppg: 12.7, pir: 12.9,
    biometrics: { stressLoad: 44, cortisol: 'optimal', bloodGlucose: 95, ketones: 0.6, vo2Max: 48, lactateThreshold: 165, muscleOxygen: 71, neuralFatigue: 'low', bodyTemp: '+0.3', respiratoryRate: 15 }},
  { id: '003667', name: 'Gabriel Deck', position: 'PF', number: 14, whoopConnected: true, ouraConnected: true, neurophoriaConnected: true, readiness: 87, strain: 12.8, hrv: 68, sleep: 7.4, ppg: 8.5, pir: 9.0,
    biometrics: { stressLoad: 36, cortisol: 'optimal', bloodGlucose: 90, ketones: 0.9, vo2Max: 47, lactateThreshold: 162, muscleOxygen: 73, neuralFatigue: 'low', bodyTemp: '+0.2', respiratoryRate: 14, mentalState: 84, focusScore: 82, flowStateReadiness: 80, umpiScore: 83 }},
  { id: '001779', name: 'Serge Ibaka', position: 'C', number: 9, whoopConnected: true, ouraConnected: false, neurophoriaConnected: false, readiness: 79, strain: 13.5, hrv: 58, sleep: 6.9, ppg: 6.5, pir: 6.4,
    biometrics: { stressLoad: 52, cortisol: 'elevated', bloodGlucose: 102, ketones: 0.3, vo2Max: 42, lactateThreshold: 150, muscleOxygen: 65, neuralFatigue: 'moderate', bodyTemp: '+0.5', respiratoryRate: 17 }},
  { id: '007318', name: 'Alberto Abalde', position: 'SG', number: 6, whoopConnected: true, ouraConnected: true, neurophoriaConnected: false, readiness: 88, strain: 10.5, hrv: 72, sleep: 7.8, ppg: 5.5, pir: 5.9,
    biometrics: { stressLoad: 28, cortisol: 'optimal', bloodGlucose: 86, ketones: 1.1, vo2Max: 51, lactateThreshold: 170, muscleOxygen: 78, neuralFatigue: 'low', bodyTemp: '+0.1', respiratoryRate: 13 }},
  { id: '002195', name: 'Sergio Llull', position: 'PG', number: 23, whoopConnected: true, ouraConnected: true, neurophoriaConnected: false, readiness: 76, strain: 10.2, hrv: 55, sleep: 7.0, ppg: 6.7, pir: 5.2,
    biometrics: { stressLoad: 55, cortisol: 'elevated', bloodGlucose: 105, ketones: 0.2, vo2Max: 45, lactateThreshold: 158, muscleOxygen: 66, neuralFatigue: 'moderate', bodyTemp: '+0.6', respiratoryRate: 18 }},
  { id: '009085', name: 'Usman Garuba', position: 'PF', number: 16, whoopConnected: false, ouraConnected: true, neurophoriaConnected: true, readiness: 94, strain: 16.5, hrv: 82, sleep: 8.5, ppg: 4.1, pir: 4.4,
    biometrics: { stressLoad: 24, cortisol: 'optimal', bloodGlucose: 84, ketones: 1.5, vo2Max: 54, lactateThreshold: 175, muscleOxygen: 80, neuralFatigue: 'low', bodyTemp: '+0.0', respiratoryRate: 12, mentalState: 96, focusScore: 94, flowStateReadiness: 92, umpiScore: 95 }},
  { id: '010987', name: 'Andrés Feliz', position: 'PG', number: 24, whoopConnected: true, ouraConnected: true, neurophoriaConnected: false, readiness: 90, strain: 12.2, hrv: 75, sleep: 8.0, ppg: 3.7, pir: 4.4,
    biometrics: { stressLoad: 30, cortisol: 'optimal', bloodGlucose: 88, ketones: 1.0, vo2Max: 46, lactateThreshold: 160, muscleOxygen: 75, neuralFatigue: 'low', bodyTemp: '+0.2', respiratoryRate: 14 }},
  { id: '011234', name: 'Theo Maledon', position: 'PG', number: 12, whoopConnected: true, ouraConnected: true, neurophoriaConnected: false, readiness: 87, strain: 12.5, hrv: 70, sleep: 7.6, ppg: 5.2, pir: 6.8,
    biometrics: { stressLoad: 35, cortisol: 'optimal', bloodGlucose: 90, ketones: 0.9, vo2Max: 49, lactateThreshold: 166, muscleOxygen: 73, neuralFatigue: 'low', bodyTemp: '+0.2', respiratoryRate: 14 }},
  { id: '011567', name: 'Trey Lyles', position: 'PF', number: 0, whoopConnected: true, ouraConnected: true, neurophoriaConnected: false, readiness: 84, strain: 13.8, hrv: 64, sleep: 7.3, ppg: 6.8, pir: 7.2,
    biometrics: { stressLoad: 42, cortisol: 'optimal', bloodGlucose: 94, ketones: 0.5, vo2Max: 46, lactateThreshold: 161, muscleOxygen: 70, neuralFatigue: 'low', bodyTemp: '+0.3', respiratoryRate: 15 }},
  { id: '010456', name: 'Hugo Gonzalez', position: 'PG', number: 3, whoopConnected: false, ouraConnected: false, neurophoriaConnected: false, readiness: null, strain: null, hrv: null, sleep: null, ppg: 1.9, pir: 1.0,
    biometrics: null },
];

// Baltimore Orioles 2024-25 roster with synthetic health data (Statcast-connected)
const ORIOLES_PLAYERS = [
  { id: '663757', name: 'Adley Rutschman', position: 'C', number: 35, whoopConnected: true, ouraConnected: true, neurophoriaConnected: true, readiness: 94, strain: 12.8, hrv: 82, sleep: 8.4, avg: .264, obp: .354, ops: .799,
    biometrics: { stressLoad: 28, cortisol: 'optimal', bloodGlucose: 88, ketones: 1.2, vo2Max: 48, lactateThreshold: 165, muscleOxygen: 78, neuralFatigue: 'low', bodyTemp: '+0.1', respiratoryRate: 13, mentalState: 94, focusScore: 92, flowStateReadiness: 88, umpiScore: 93 }},
  { id: '671739', name: 'Gunnar Henderson', position: 'SS', number: 2, whoopConnected: true, ouraConnected: true, neurophoriaConnected: true, readiness: 96, strain: 14.5, hrv: 85, sleep: 8.6, avg: .280, obp: .364, ops: .915,
    biometrics: { stressLoad: 24, cortisol: 'optimal', bloodGlucose: 85, ketones: 1.4, vo2Max: 54, lactateThreshold: 172, muscleOxygen: 82, neuralFatigue: 'low', bodyTemp: '+0.0', respiratoryRate: 12, mentalState: 97, focusScore: 95, flowStateReadiness: 93, umpiScore: 96 }},
  { id: '682998', name: 'Jackson Holliday', position: '2B', number: 7, whoopConnected: true, ouraConnected: true, neurophoriaConnected: false, readiness: 91, strain: 13.2, hrv: 78, sleep: 8.1, avg: .238, obp: .310, ops: .672,
    biometrics: { stressLoad: 32, cortisol: 'optimal', bloodGlucose: 90, ketones: 1.0, vo2Max: 52, lactateThreshold: 168, muscleOxygen: 76, neuralFatigue: 'low', bodyTemp: '+0.2', respiratoryRate: 14 }},
  { id: '669955', name: 'Colton Cowser', position: 'LF', number: 17, whoopConnected: true, ouraConnected: false, neurophoriaConnected: false, readiness: 88, strain: 11.5, hrv: 72, sleep: 7.8, avg: .245, obp: .334, ops: .752,
    biometrics: { stressLoad: 36, cortisol: 'optimal', bloodGlucose: 92, ketones: 0.9, vo2Max: 50, lactateThreshold: 164, muscleOxygen: 74, neuralFatigue: 'low', bodyTemp: '+0.2', respiratoryRate: 14 }},
  { id: '605141', name: 'Anthony Santander', position: 'RF', number: 25, whoopConnected: true, ouraConnected: true, neurophoriaConnected: true, readiness: 85, strain: 12.2, hrv: 68, sleep: 7.4, avg: .273, obp: .340, ops: .906,
    biometrics: { stressLoad: 42, cortisol: 'optimal', bloodGlucose: 94, ketones: 0.7, vo2Max: 46, lactateThreshold: 160, muscleOxygen: 72, neuralFatigue: 'low', bodyTemp: '+0.3', respiratoryRate: 15, mentalState: 86, focusScore: 84, flowStateReadiness: 80, umpiScore: 85 }},
  { id: '656811', name: 'Ryan Mountcastle', position: '1B', number: 6, whoopConnected: true, ouraConnected: true, neurophoriaConnected: false, readiness: 83, strain: 11.8, hrv: 65, sleep: 7.2, avg: .259, obp: .308, ops: .773,
    biometrics: { stressLoad: 45, cortisol: 'elevated', bloodGlucose: 96, ketones: 0.5, vo2Max: 44, lactateThreshold: 158, muscleOxygen: 70, neuralFatigue: 'moderate', bodyTemp: '+0.4', respiratoryRate: 16 }},
  { id: '675911', name: 'Heston Kjerstad', position: 'DH', number: 13, whoopConnected: true, ouraConnected: false, neurophoriaConnected: false, readiness: 89, strain: 10.8, hrv: 74, sleep: 7.9, avg: .278, obp: .343, ops: .832,
    biometrics: { stressLoad: 34, cortisol: 'optimal', bloodGlucose: 88, ketones: 1.1, vo2Max: 49, lactateThreshold: 166, muscleOxygen: 75, neuralFatigue: 'low', bodyTemp: '+0.2', respiratoryRate: 14 }},
  { id: '677594', name: 'Jordan Westburg', position: '3B', number: 11, whoopConnected: true, ouraConnected: true, neurophoriaConnected: true, readiness: 90, strain: 13.5, hrv: 76, sleep: 8.0, avg: .261, obp: .318, ops: .804,
    biometrics: { stressLoad: 30, cortisol: 'optimal', bloodGlucose: 87, ketones: 1.0, vo2Max: 51, lactateThreshold: 169, muscleOxygen: 77, neuralFatigue: 'low', bodyTemp: '+0.1', respiratoryRate: 13, mentalState: 91, focusScore: 89, flowStateReadiness: 86, umpiScore: 90 }},
  { id: '656555', name: 'Cedric Mullins', position: 'CF', number: 31, whoopConnected: true, ouraConnected: true, neurophoriaConnected: false, readiness: 86, strain: 14.2, hrv: 70, sleep: 7.5, avg: .232, obp: .290, ops: .680,
    biometrics: { stressLoad: 40, cortisol: 'optimal', bloodGlucose: 91, ketones: 0.8, vo2Max: 56, lactateThreshold: 175, muscleOxygen: 80, neuralFatigue: 'low', bodyTemp: '+0.2', respiratoryRate: 13 }},
  { id: '669720', name: 'Corbin Burnes', position: 'P', number: 39, whoopConnected: true, ouraConnected: true, neurophoriaConnected: true, readiness: 87, strain: 16.5, hrv: 71, sleep: 7.6, era: 2.92, whip: 1.09, kPer9: 9.2,
    biometrics: { stressLoad: 48, cortisol: 'elevated', bloodGlucose: 98, ketones: 0.6, vo2Max: 47, lactateThreshold: 162, muscleOxygen: 71, neuralFatigue: 'moderate', bodyTemp: '+0.4', respiratoryRate: 15, mentalState: 88, focusScore: 90, flowStateReadiness: 82, umpiScore: 87 }},
  { id: '621244', name: 'Grayson Rodriguez', position: 'P', number: 30, whoopConnected: true, ouraConnected: false, neurophoriaConnected: false, readiness: 84, strain: 15.8, hrv: 67, sleep: 7.3, era: 4.07, whip: 1.24, kPer9: 8.8,
    biometrics: { stressLoad: 52, cortisol: 'elevated', bloodGlucose: 100, ketones: 0.4, vo2Max: 45, lactateThreshold: 158, muscleOxygen: 68, neuralFatigue: 'moderate', bodyTemp: '+0.5', respiratoryRate: 16 }},
  { id: '656427', name: 'Dean Kremer', position: 'P', number: 64, whoopConnected: true, ouraConnected: true, neurophoriaConnected: false, readiness: 82, strain: 14.8, hrv: 64, sleep: 7.1, era: 4.81, whip: 1.31, kPer9: 7.9,
    biometrics: { stressLoad: 55, cortisol: 'elevated', bloodGlucose: 102, ketones: 0.3, vo2Max: 43, lactateThreshold: 155, muscleOxygen: 66, neuralFatigue: 'moderate', bodyTemp: '+0.5', respiratoryRate: 17 }},
  { id: '656302', name: 'Felix Bautista', position: 'P', number: 74, whoopConnected: false, ouraConnected: false, neurophoriaConnected: false, readiness: null, strain: null, hrv: null, sleep: null, era: 1.42, whip: 0.79, saves: 33,
    biometrics: null },
];

// Sport-specific configuration
const SPORT_CONFIG = {
  basketball: {
    players: REAL_MADRID_PLAYERS,
    teamName: 'Real Madrid',
    statLabels: { primary: 'PPG', secondary: 'PIR' },
    statFields: { primary: 'ppg', secondary: 'pir' },
  },
  baseball: {
    players: ORIOLES_PLAYERS,
    teamName: 'Baltimore Orioles',
    statLabels: { primary: 'AVG', secondary: 'OPS' },
    statFields: { primary: 'avg', secondary: 'ops' },
  },
};

// Integrations config - dynamic based on players
const getIntegrations = (players) => [
  {
    id: 'whoop',
    name: 'WHOOP',
    description: 'Track strain, recovery, and sleep metrics',
    logo: '/images/logos/whoop.svg',
    gradient: 'from-emerald-500 to-green-600',
    glowColor: 'rgba(16, 185, 129, 0.4)',
    features: ['Recovery Score', 'Strain Tracking', 'Sleep Performance', 'HRV Monitoring'],
    status: 'connected',
    connectedCount: players.filter(p => p.whoopConnected).length,
  },
  {
    id: 'oura',
    name: 'Oura Ring',
    description: 'Monitor sleep quality, readiness, and activity',
    logo: '/images/logos/oura.svg',
    gradient: 'from-violet-500 to-purple-600',
    glowColor: 'rgba(139, 92, 246, 0.4)',
    features: ['Readiness Score', 'Sleep Stages', 'Activity Goals', 'Body Temperature'],
    status: 'connected',
    connectedCount: players.filter(p => p.ouraConnected).length,
  },
  {
    id: 'neuphoria',
    name: 'Neuphoria',
    description: 'EEG neurofeedback and mental performance monitoring',
    logo: '/images/logos/neuphoria.svg',
    gradient: 'from-cyan-400 to-blue-600',
    glowColor: 'rgba(0, 217, 255, 0.4)',
    features: ['Mental State', 'Focus Score', 'Flow State Readiness', 'Neural Fatigue'],
    status: 'connected',
    connectedCount: players.filter(p => p.neurophoriaConnected).length,
  },
  {
    id: 'garmin',
    name: 'Garmin',
    description: 'GPS and fitness tracking integration',
    logo: '/images/logos/garmin.svg',
    gradient: 'from-blue-500 to-cyan-600',
    glowColor: 'rgba(59, 130, 246, 0.4)',
    features: ['GPS Tracking', 'Heart Rate', 'Training Load', 'Body Battery'],
    status: 'coming_soon',
    connectedCount: 0,
  },
  {
    id: 'apple',
    name: 'Apple Health',
    description: 'Sync health data from Apple ecosystem',
    logo: '/images/logos/apple.svg',
    gradient: 'from-gray-600 to-gray-800',
    glowColor: 'rgba(107, 114, 128, 0.4)',
    features: ['Activity Rings', 'Heart Rate', 'Sleep', 'Workouts'],
    status: 'coming_soon',
    connectedCount: 0,
  },
];

// Glassmorphism card component
const GlassCard = ({ children, className = '', glow = false, glowColor = 'rgba(255, 215, 0, 0.1)' }) => (
  <div
    className={`relative rounded-xl border border-white/[0.08] bg-[#0d0d12]/80 backdrop-blur-xl ${className}`}
    style={{
      boxShadow: glow
        ? `0 0 40px ${glowColor}, 0 4px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)`
        : '0 4px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
    }}
  >
    {children}
  </div>
);

// Neon stat badge
const NeonStat = ({ value, label, color = 'green', size = 'md' }) => {
  const colorMap = {
    green: { text: 'text-emerald-400', glow: '0 0 20px rgba(16, 185, 129, 0.5)', bg: 'bg-emerald-500/10' },
    yellow: { text: 'text-amber-400', glow: '0 0 20px rgba(251, 191, 36, 0.5)', bg: 'bg-amber-500/10' },
    red: { text: 'text-red-400', glow: '0 0 20px rgba(239, 68, 68, 0.5)', bg: 'bg-red-500/10' },
    blue: { text: 'text-blue-400', glow: '0 0 20px rgba(59, 130, 246, 0.5)', bg: 'bg-blue-500/10' },
    purple: { text: 'text-violet-400', glow: '0 0 20px rgba(139, 92, 246, 0.5)', bg: 'bg-violet-500/10' },
  };
  const c = colorMap[color] || colorMap.green;
  const textSize = size === 'lg' ? 'text-3xl' : size === 'sm' ? 'text-lg' : 'text-2xl';

  return (
    <div className={`${c.bg} rounded-lg p-3 text-center`}>
      <div className={`${textSize} font-bold ${c.text}`} style={{ textShadow: c.glow }}>
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-white/40 mt-1">{label}</div>
    </div>
  );
};

// Player avatar with headshot (sport-aware)
const PlayerAvatar = ({ player, size = 'md', showStatus = false, sport = 'basketball' }) => {
  const [imgError, setImgError] = useState(false);
  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-12 h-12 text-sm',
    lg: 'w-16 h-16 text-lg',
    xl: 'w-20 h-20 text-xl',
  };
  const initials = player.name.split(' ').map(n => n[0]).join('');

  return (
    <div className="relative">
      <div className={`${sizeClasses[size]} rounded-full overflow-hidden ring-2 ring-white/10 bg-[#1a1a20]`}>
        {!imgError ? (
          <img
            src={getPlayerHeadshot(player.number, player.name, sport)}
            alt={player.name}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-semibold text-white/60">
            {initials}
          </div>
        )}
      </div>
      {showStatus && (
        <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#0d0d12] ${
          player.readiness >= 85 ? 'bg-emerald-500' : player.readiness >= 70 ? 'bg-amber-500' : player.readiness ? 'bg-red-500' : 'bg-gray-500'
        }`} />
      )}
    </div>
  );
};

// Resize handle
const ResizeHandle = ({ direction = 'vertical' }) => (
  <PanelResizeHandle className={`group relative flex items-center justify-center ${
    direction === 'vertical' ? 'w-2 hover:w-3' : 'h-2 hover:h-3'
  } transition-all`}>
    <div className={`${direction === 'vertical' ? 'h-12 w-1' : 'w-12 h-1'} rounded-full bg-white/10 group-hover:bg-amber-500/50 transition-colors`} />
  </PanelResizeHandle>
);

const HealthIntegrations = ({ sport: sportProp }) => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // Detect sport from prop or URL path
  const sport = sportProp || (location.pathname.includes('/baseball') ? 'baseball' : 'basketball');
  const sportConfig = SPORT_CONFIG[sport] || SPORT_CONFIG.basketball;
  const teamPlayers = sportConfig.players;
  const integrations = useMemo(() => getIntegrations(teamPlayers), [teamPlayers]);

  // Stats calculations
  const stats = useMemo(() => ({
    connected: teamPlayers.filter(p => p.whoopConnected || p.ouraConnected).length,
    total: teamPlayers.length,
    avgReadiness: Math.round(
      teamPlayers.filter(p => p.readiness).reduce((sum, p) => sum + p.readiness, 0) /
      teamPlayers.filter(p => p.readiness).length
    ),
    lowReadiness: teamPlayers.filter(p => p.readiness && p.readiness < 70).length,
    notConnected: teamPlayers.filter(p => !p.whoopConnected && !p.ouraConnected).length,
  }), [teamPlayers]);

  const getReadinessColor = (score) => {
    if (!score) return 'gray';
    if (score >= 85) return 'green';
    if (score >= 70) return 'yellow';
    return 'red';
  };

  const tabs = [
    { id: 'overview', label: 'Team Overview', icon: Users },
    { id: 'integrations', label: 'Integrations', icon: Link2 },
    { id: 'player', label: 'Player Details', icon: Heart },
  ];

  // Team Overview Tab
  const TeamOverviewTab = () => (
    <div className="flex flex-col h-full gap-4 overflow-hidden">
      {/* Stats Row */}
      <div className="flex-none grid grid-cols-4 gap-3">
        <GlassCard className="p-4" glow glowColor="rgba(59, 130, 246, 0.15)">
          <div className="flex items-center gap-2 mb-2">
            <Wifi className="text-blue-400" size={16} />
            <span className="text-[10px] uppercase tracking-wider text-white/50">Connected</span>
          </div>
          <div className="text-3xl font-bold text-white">{stats.connected}<span className="text-white/30">/{stats.total}</span></div>
        </GlassCard>
        <GlassCard className="p-4" glow glowColor="rgba(16, 185, 129, 0.15)">
          <div className="flex items-center gap-2 mb-2">
            <Battery className="text-emerald-400" size={16} />
            <span className="text-[10px] uppercase tracking-wider text-white/50">Avg Readiness</span>
          </div>
          <div className="text-3xl font-bold text-emerald-400" style={{ textShadow: '0 0 20px rgba(16, 185, 129, 0.5)' }}>
            {stats.avgReadiness}%
          </div>
        </GlassCard>
        <GlassCard className="p-4" glow glowColor="rgba(251, 191, 36, 0.15)">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="text-amber-400" size={16} />
            <span className="text-[10px] uppercase tracking-wider text-white/50">Low Readiness</span>
          </div>
          <div className="text-3xl font-bold text-amber-400" style={{ textShadow: '0 0 20px rgba(251, 191, 36, 0.5)' }}>
            {stats.lowReadiness}
          </div>
        </GlassCard>
        <GlassCard className="p-4" glow glowColor="rgba(239, 68, 68, 0.15)">
          <div className="flex items-center gap-2 mb-2">
            <WifiOff className="text-red-400" size={16} />
            <span className="text-[10px] uppercase tracking-wider text-white/50">Not Connected</span>
          </div>
          <div className="text-3xl font-bold text-red-400" style={{ textShadow: '0 0 20px rgba(239, 68, 68, 0.5)' }}>
            {stats.notConnected}
          </div>
        </GlassCard>
      </div>

      {/* Players Grid - scrollable */}
      <GlassCard className="flex-1 overflow-hidden flex flex-col">
        <div className="flex-none px-4 py-3 border-b border-white/[0.06]">
          <h3 className="text-sm font-semibold text-white">Team Health Status</h3>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-4">
            {teamPlayers.map(player => (
              <button
                key={player.id}
                onClick={() => { setSelectedPlayer(player); setActiveTab('player'); }}
                className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] hover:bg-white/[0.06] border border-transparent hover:border-amber-500/30 transition-all group text-left"
              >
                <PlayerAvatar player={player} size="md" showStatus sport={sport} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white truncate group-hover:text-amber-400 transition-colors">
                    {player.name}
                  </div>
                  <div className="text-xs text-white/40">#{player.number} · {player.position}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1.5">
                    {player.whoopConnected ? (
                      <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center" title="WHOOP">
                        <CheckCircle className="text-emerald-400" size={12} />
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center" title="WHOOP">
                        <XCircle className="text-white/20" size={12} />
                      </div>
                    )}
                    {player.ouraConnected ? (
                      <div className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center" title="Oura">
                        <CheckCircle className="text-violet-400" size={12} />
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center" title="Oura">
                        <XCircle className="text-white/20" size={12} />
                      </div>
                    )}
                    {player.neurophoriaConnected ? (
                      <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center" title="Neuphoria">
                        <CheckCircle className="text-cyan-400" size={12} />
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center" title="Neuphoria">
                        <XCircle className="text-white/20" size={12} />
                      </div>
                    )}
                  </div>
                  {player.readiness && (
                    <div className={`text-lg font-bold ${
                      player.readiness >= 85 ? 'text-emerald-400' : player.readiness >= 70 ? 'text-amber-400' : 'text-red-400'
                    }`} style={{ textShadow: player.readiness >= 85 ? '0 0 10px rgba(16, 185, 129, 0.5)' : '' }}>
                      {player.readiness}%
                    </div>
                  )}
                  <ChevronRight className="text-white/20 group-hover:text-amber-400 transition-colors" size={16} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </GlassCard>
    </div>
  );

  // Integrations Tab
  const IntegrationsTab = () => (
    <div className="grid grid-cols-2 gap-4 h-full overflow-y-auto p-1">
      {integrations.map(integration => (
        <GlassCard
          key={integration.id}
          className="overflow-hidden flex flex-col"
          glow={integration.status === 'connected'}
          glowColor={integration.glowColor}
        >
          {/* Header */}
          <div className={`p-4 bg-gradient-to-r ${integration.gradient}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center text-2xl font-bold text-white">
                  {integration.name[0]}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{integration.name}</h3>
                  {integration.status === 'connected' ? (
                    <div className="flex items-center gap-1.5 text-white/80 text-sm">
                      <CheckCircle size={14} />
                      <span>{integration.connectedCount} players connected</span>
                    </div>
                  ) : (
                    <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full text-white">
                      Coming Soon
                    </span>
                  )}
                </div>
              </div>
              <Watch className="text-white/30" size={28} />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-4 flex flex-col">
            <p className="text-white/50 text-sm mb-4">{integration.description}</p>
            <div className="flex-1 grid grid-cols-2 gap-2 mb-4">
              {integration.features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <Sparkles className="text-amber-500/60" size={12} />
                  <span className="text-white/70">{feature}</span>
                </div>
              ))}
            </div>
            <button
              disabled={integration.status === 'coming_soon'}
              className={`w-full py-2.5 rounded-lg font-medium transition-all ${
                integration.status === 'coming_soon'
                  ? 'bg-white/5 text-white/30 cursor-not-allowed'
                  : 'bg-gradient-to-r from-amber-500 to-orange-500 text-black hover:shadow-[0_0_30px_rgba(251,191,36,0.4)]'
              }`}
            >
              {integration.status === 'coming_soon' ? 'Coming Soon' : 'Configure'}
            </button>
          </div>
        </GlassCard>
      ))}
    </div>
  );

  // Player Details Tab with Resizable Panels
  const PlayerDetailsTab = () => {
    const player = selectedPlayer || teamPlayers[0];

    return (
      <div className="h-full flex flex-col gap-4 overflow-hidden">
        {/* Player Selector */}
        <GlassCard className="flex-none p-3">
          <div className="flex items-center gap-4">
            <select
              value={player?.id || ''}
              onChange={(e) => setSelectedPlayer(teamPlayers.find(p => p.id === e.target.value))}
              className="flex-1 md:flex-none md:w-72 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30 transition-all"
            >
              {teamPlayers.map(p => (
                <option key={p.id} value={p.id} className="bg-[#0d0d12]">
                  #{p.number} {p.name} ({p.position})
                </option>
              ))}
            </select>
            <button className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/50 hover:text-amber-400 transition-colors">
              <RefreshCw size={18} />
            </button>
          </div>
        </GlassCard>

        {/* Resizable Panels */}
        {player && (
          <PanelGroup direction="horizontal" className="flex-1">
            {/* Left Panel - Player Info & Core Metrics */}
            <Panel defaultSize={35} minSize={25}>
              <div className="h-full flex flex-col gap-4 pr-1 overflow-y-auto">
                {/* Player Header */}
                <GlassCard className="p-4" glow glowColor="rgba(255, 215, 0, 0.08)">
                  <div className="flex items-center gap-4">
                    <PlayerAvatar player={player} size="xl" sport={sport} />
                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-white">{player.name}</h2>
                      <p className="text-white/40 text-sm">#{player.number} · {player.position} · {sportConfig.teamName}</p>
                      <div className="flex gap-2 mt-2">
                        {player.whoopConnected && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                            WHOOP
                          </span>
                        )}
                        {player.ouraConnected && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-400 border border-violet-500/30">
                            Oura
                          </span>
                        )}
                        {player.neurophoriaConnected && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                            Neuphoria
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-4xl font-bold ${
                        player.readiness >= 85 ? 'text-emerald-400' : player.readiness >= 70 ? 'text-amber-400' : 'text-red-400'
                      }`} style={{ textShadow: `0 0 30px ${player.readiness >= 85 ? 'rgba(16,185,129,0.6)' : player.readiness >= 70 ? 'rgba(251,191,36,0.6)' : 'rgba(239,68,68,0.6)'}` }}>
                        {player.readiness || '--'}%
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-white/40">Readiness</div>
                    </div>
                  </div>
                </GlassCard>

                {/* Core Metrics */}
                <GlassCard className="p-4">
                  <h3 className="text-[10px] uppercase tracking-wider text-amber-500 mb-3 font-semibold">Core Metrics</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <NeonStat value={player.strain || '--'} label="Strain" color="blue" />
                    <NeonStat value={player.hrv ? `${player.hrv}ms` : '--'} label="HRV" color="purple" />
                    <NeonStat value={player.sleep ? `${player.sleep}h` : '--'} label="Sleep" color="blue" />
                    <NeonStat
                      value={player[sportConfig.statFields.primary] || '--'}
                      label={sportConfig.statLabels.primary}
                      color="yellow"
                    />
                  </div>
                </GlassCard>

                {/* AI Insight */}
                <GlassCard className="p-4" glow glowColor="rgba(139, 92, 246, 0.1)">
                  <div className="flex items-start gap-3">
                    <Sparkles className="text-violet-400 flex-shrink-0 mt-0.5" size={18} />
                    <div>
                      <h4 className="text-xs font-semibold text-violet-400 mb-1">AI Recovery Insight</h4>
                      <p className="text-sm text-white/60">
                        {player.readiness >= 85
                          ? 'Excellent recovery. Optimal for high-intensity training or competition.'
                          : player.readiness >= 70
                          ? 'Moderate recovery. Consider lighter practice or skills work today.'
                          : player.readiness
                          ? 'Low recovery detected. Prioritize rest and recovery activities.'
                          : 'No wearable data available. Connect devices to enable insights.'}
                      </p>
                    </div>
                  </div>
                </GlassCard>
              </div>
            </Panel>

            <ResizeHandle direction="vertical" />

            {/* Right Panel - Biometrics */}
            <Panel defaultSize={65} minSize={40}>
              <div className="h-full pl-1 overflow-y-auto">
                {player.biometrics ? (
                  <GlassCard className="h-full p-4">
                    <h3 className="text-[10px] uppercase tracking-wider text-amber-500 mb-4 font-semibold flex items-center gap-2">
                      <TrendingUp size={14} />
                      Biohacking Metrics
                    </h3>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                        <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Stress Load</div>
                        <div className={`text-xl font-bold ${
                          player.biometrics.stressLoad < 40 ? 'text-emerald-400' : player.biometrics.stressLoad < 50 ? 'text-amber-400' : 'text-red-400'
                        }`}>{player.biometrics.stressLoad}%</div>
                      </div>
                      <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                        <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Cortisol</div>
                        <div className={`text-xl font-bold capitalize ${
                          player.biometrics.cortisol === 'optimal' ? 'text-emerald-400' : 'text-amber-400'
                        }`}>{player.biometrics.cortisol}</div>
                      </div>
                      <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                        <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Blood Glucose</div>
                        <div className="text-xl font-bold text-white">{player.biometrics.bloodGlucose} <span className="text-sm text-white/40">mg/dL</span></div>
                      </div>
                      <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                        <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Ketones</div>
                        <div className={`text-xl font-bold ${player.biometrics.ketones >= 1.0 ? 'text-emerald-400' : 'text-white'}`}>
                          {player.biometrics.ketones} <span className="text-sm text-white/40">mmol/L</span>
                        </div>
                      </div>
                      <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                        <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">VO2 Max</div>
                        <div className="text-xl font-bold text-blue-400">{player.biometrics.vo2Max} <span className="text-sm text-white/40">ml/kg/min</span></div>
                      </div>
                      <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                        <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Lactate Threshold</div>
                        <div className="text-xl font-bold text-violet-400">{player.biometrics.lactateThreshold} <span className="text-sm text-white/40">bpm</span></div>
                      </div>
                      <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                        <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Muscle O₂</div>
                        <div className={`text-xl font-bold ${player.biometrics.muscleOxygen >= 75 ? 'text-emerald-400' : 'text-white'}`}>
                          {player.biometrics.muscleOxygen}%
                        </div>
                      </div>
                      <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                        <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Neural Fatigue</div>
                        <div className={`text-xl font-bold capitalize ${
                          player.biometrics.neuralFatigue === 'low' ? 'text-emerald-400' : 'text-amber-400'
                        }`}>{player.biometrics.neuralFatigue}</div>
                      </div>
                      <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                        <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Body Temp</div>
                        <div className="text-xl font-bold text-orange-400">{player.biometrics.bodyTemp}°F</div>
                      </div>
                      <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                        <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Resp. Rate</div>
                        <div className="text-xl font-bold text-cyan-400">{player.biometrics.respiratoryRate} <span className="text-sm text-white/40">/min</span></div>
                      </div>
                    </div>

                    {/* Neuphoria Mental Performance Metrics */}
                    {player.neurophoriaConnected && player.biometrics.mentalState && (
                      <>
                        <h3 className="text-[10px] uppercase tracking-wider text-cyan-400 mt-6 mb-4 font-semibold flex items-center gap-2">
                          <Activity size={14} />
                          Neuphoria Mental Performance
                        </h3>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                          <div className="p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/20">
                            <div className="text-[10px] uppercase tracking-wider text-cyan-400/60 mb-1">Mental State</div>
                            <div className={`text-xl font-bold ${
                              player.biometrics.mentalState >= 85 ? 'text-cyan-400' : player.biometrics.mentalState >= 70 ? 'text-amber-400' : 'text-red-400'
                            }`} style={{ textShadow: '0 0 10px rgba(0, 217, 255, 0.4)' }}>{player.biometrics.mentalState}%</div>
                          </div>
                          <div className="p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/20">
                            <div className="text-[10px] uppercase tracking-wider text-cyan-400/60 mb-1">Focus Score</div>
                            <div className={`text-xl font-bold ${
                              player.biometrics.focusScore >= 85 ? 'text-cyan-400' : player.biometrics.focusScore >= 70 ? 'text-amber-400' : 'text-red-400'
                            }`} style={{ textShadow: '0 0 10px rgba(0, 217, 255, 0.4)' }}>{player.biometrics.focusScore}%</div>
                          </div>
                          <div className="p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/20">
                            <div className="text-[10px] uppercase tracking-wider text-cyan-400/60 mb-1">Flow State</div>
                            <div className={`text-xl font-bold ${
                              player.biometrics.flowStateReadiness >= 85 ? 'text-cyan-400' : player.biometrics.flowStateReadiness >= 70 ? 'text-amber-400' : 'text-white'
                            }`} style={{ textShadow: '0 0 10px rgba(0, 217, 255, 0.4)' }}>{player.biometrics.flowStateReadiness}%</div>
                          </div>
                          <div className="p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/20">
                            <div className="text-[10px] uppercase tracking-wider text-cyan-400/60 mb-1">UMPI Score</div>
                            <div className="text-xl font-bold text-cyan-400" style={{ textShadow: '0 0 10px rgba(0, 217, 255, 0.4)' }}>{player.biometrics.umpiScore}</div>
                          </div>
                        </div>
                      </>
                    )}
                  </GlassCard>
                ) : (
                  <GlassCard className="h-full flex items-center justify-center">
                    <div className="text-center p-8">
                      <WifiOff className="mx-auto mb-4 text-white/20" size={48} />
                      <p className="text-white/40">No biometric data available</p>
                      <p className="text-white/20 text-sm mt-2">Connect wearable devices to view metrics</p>
                    </div>
                  </GlassCard>
                )}
              </div>
            </Panel>
          </PanelGroup>
        )}
      </div>
    );
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: '#050507' }}>
      {/* Header - Fixed */}
      <div className="flex-none p-4 border-b border-white/[0.06]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-red-500/20 to-pink-500/20 border border-red-500/30">
              <Heart className="text-red-400" size={24} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Health Integrations</h1>
              <p className="text-white/40 text-sm">Wearable device monitoring and recovery analytics</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-black shadow-[0_0_20px_rgba(251,191,36,0.3)]'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                }`}
              >
                <tab.icon size={16} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content - Flex grow with overflow control */}
      <div className="flex-1 p-4 overflow-hidden">
        {activeTab === 'overview' && <TeamOverviewTab />}
        {activeTab === 'integrations' && <IntegrationsTab />}
        {activeTab === 'player' && <PlayerDetailsTab />}
      </div>
    </div>
  );
};

export default HealthIntegrations;
